import type { AttachmentFileList } from 'owa-attachment-file-types';
import { lazyLogAttachOrShareDatapoint } from 'owa-attachment-filepicker';
import type { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { createAttachments } from 'owa-mail-compose-command';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { mailStore } from 'owa-mail-store';
import { SmartDocOption } from 'owa-mail-smart-pill-features-types/lib/schema/SmartDocOption';
import { logUsage } from 'owa-analytics';
import {
    lazyInsertInlineImages,
    lazyCreateObjectUrlsAsTempId,
} from 'owa-editor-inlineimage-plugin';
import {
    lazyLogEntitiesFromFilePicker,
    lazyGetAttachmentSuggestionsTraceId,
} from 'owa-attachment-suggestions';

// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
export async function aFPCreateAttachmentHandler(
    files: AttachmentFileList,
    viewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    dataProvider: AttachmentDataProviderType
): Promise<void> {
    // Set fromSmartDoc parameter in properties
    const isAddedFromSmartDoc: boolean =
        viewState.smartPillViewState.smartDocOption != SmartDocOption.None;
    properties.isAddedFromSmartDoc = isAddedFromSmartDoc;

    // set tempId to each file in the list of files
    // it is needed to find and update the cid values in the placeholder img elements
    if (properties.isInline && files.length > 0) {
        const createObjectUrlsAsTempId = await lazyCreateObjectUrlsAsTempId.import();
        await createObjectUrlsAsTempId(files, viewState.inlineImageV2);
    }

    // add the files to attachment queue manager
    // it removes any file that should not be uploaded
    // (too large or non-image file as inline image)
    files = await createAttachments(
        files,
        viewState,
        properties,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (49,9): Argument of type '(() => void) | null' is not assignable to parameter of type '((attachmentId: ClientAttachmentId) => void) | undefined'.
        // @ts-expect-error
        isAddedFromSmartDoc
            ? () => logSmartDocAttachmentsAdded(viewState)
            : null /* onAllAttachmentsProcessed */,
        null /* onAttachmentCreatedCallback */,
        null /* onAttachmentCanceledOrFailedCallback */,
        null /* tryAgainCallback */
    );

    // call insertInlineImages() using the filtered files list
    if (properties.isInline && files.length > 0) {
        const insertInlineImages = await lazyInsertInlineImages.import();
        insertInlineImages(files, viewState, viewState.inlineImageV2);
    }

    const logAttachOrShareDatapoint = await lazyLogAttachOrShareDatapoint.import();
    const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
    const traceId = getAttachmentSuggestionsTraceId(viewState.referenceItemId);
    logAttachOrShareDatapoint(
        {
            files: files,
            isInline: properties.isInline,
            shouldShare: properties.shouldShare,
            dataProviderType: dataProvider,
        },
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2345 (76,9): Argument of type 'string | null' is not assignable to parameter of type 'string | undefined'.
        // @ts-expect-error
        traceId
    );

    lazyLogEntitiesFromFilePicker.importAndExecute(
        files,
        viewState.referenceItemId,
        viewState.suggestedAttachmentSessionId
    );
}

function logSmartDocAttachmentsAdded(viewState: ComposeViewState) {
    const referenceItem = mailStore.items.get(viewState.referenceItemId?.Id);

    let extractionId: string = '';
    if (referenceItem?.SmartDocData?.EntityId) {
        extractionId = referenceItem.SmartDocData.EntityId;
    }

    logUsage(
        'SmartDocAttachmentsAdded',
        { smartDocExtractionId: extractionId },
        { isCore: true, excludeFromKusto: true }
    );

    // Reset the SmartDocOption as user may add more attachments in the compose form, which should
    // be logged as "not fromSmartDoc".
    viewState.smartPillViewState.smartDocOption = SmartDocOption.None;
}
